import { CalendarIcon, ClockIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { Button } from "@/shared/components/ui/button"
import { Calendar } from "@/shared/components/ui/calendar"
import { Input } from "@/shared/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover"
import { cn } from "@/shared/lib/utils"
import { formatClock, formatLocalIsoDate, parseLocalIsoDate } from "@/shared/utils/time"

// Emits a datetime-local string ("YYYY-MM-DDTHH:mm") for the sleep schema transform.
const DATE_FMT = new Intl.DateTimeFormat("sv-SE", {
  day: "numeric",
  month: "short",
  year: "numeric",
})

// Parse "YYYY-MM-DDTHH:mm" as local to avoid a UTC day shift.
function parse(value: string): { date?: Date; time: string } {
  if (!value) return { time: "" }
  const [datePart, timePart = ""] = value.split("T")
  return { date: parseLocalIsoDate(datePart), time: timePart.slice(0, 5) }
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n))
const pad = (n: number) => String(n).padStart(2, "0")

// Coerce up to 4 typed digits (HHMM, left-to-right) into a valid "HH:mm".
// A single trailing minute digit is read as tens (e.g. "134" → "13:40").
// Exported for tests.
export function normalizeTime(digits: string): string {
  if (!digits) return ""
  const h = clamp(Number(digits.slice(0, 2)), 0, 23)
  const rest = digits.slice(2)
  const m = rest.length === 1 ? clamp(Number(rest) * 10, 0, 59) : clamp(Number(rest), 0, 59)
  return `${pad(h)}:${pad(m)}`
}

// Digit-only time input: numeric keypad on mobile, colon auto-inserted after
// two digits. Emits a complete "HH:mm" once four digits are in or on blur.
function TimeField({ value, onChange }: { value: string; onChange: (time: string) => void }) {
  const [text, setText] = useState(value)

  // Re-sync when the datetime is changed from outside (reset, date pick, clear).
  useEffect(() => setText(value), [value])

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4)
    setText(digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits)
    if (digits.length === 4) onChange(normalizeTime(digits))
  }

  const handleBlur = () => {
    const norm = normalizeTime(text.replace(/\D/g, "").slice(0, 4))
    setText(norm)
    onChange(norm)
  }

  return (
    <div className="relative w-28 shrink-0">
      <ClockIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        inputMode="numeric"
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="--:--"
        maxLength={5}
        className="pl-9"
        aria-label="Tid"
      />
    </div>
  )
}

interface DateTimePickerProps {
  value: string
  onChange: (value: string) => void
  clearable?: boolean
  clearLabel?: string
}

export function DateTimePicker({
  value,
  onChange,
  clearable,
  clearLabel = "Rensa",
}: DateTimePickerProps) {
  const [open, setOpen] = useState(false)
  const { date, time } = parse(value)
  const today = new Date()

  const emit = (nextDate: Date | undefined, nextTime: string) => {
    if (!nextDate && !nextTime) {
      onChange("")
      return
    }
    onChange(`${formatLocalIsoDate(nextDate ?? new Date())}T${nextTime || formatClock(new Date())}`)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn(
                "min-w-0 flex-1 justify-start font-normal",
                !date && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="shrink-0" />
              <span className="truncate">{date ? DATE_FMT.format(date) : "Välj datum"}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              defaultMonth={date ?? today}
              disabled={{ after: today }}
              onSelect={(d) => {
                if (d) {
                  emit(d, time)
                  setOpen(false)
                }
              }}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        <TimeField value={time} onChange={(t) => emit(date, t)} />
      </div>
      {clearable && value && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange("")}
          className="h-auto self-start px-1 py-0 text-muted-foreground text-xs hover:text-foreground"
        >
          {clearLabel}
        </Button>
      )}
    </div>
  )
}
